<main class="survey-admin survey-types-list" role="main" aria-label="Survey types list">
  <header class="survey-admin__header">
    <h1 class="survey-admin__title">Survey Types</h1>
    <p class="survey-admin__desc">View and manage survey types. Create a new survey or edit an existing one.</p>
    <div class="survey-admin__actions">
      <a class="btn btn-primary survey-admin__btn" routerLink="/admin/survey-types/create">
        Create new survey
      </a>
    </div>
  </header>

  <section class="survey-types-list__table-wrap" aria-label="List of survey types">
    <table class="survey-table" role="table" aria-label="Survey types">
      <caption class="sr-only">Survey types with name, description, and actions</caption>
      <thead>
        <tr>
          <th scope="col" class="survey-table__th survey-table__th--name">Name</th>
          <th scope="col" class="survey-table__th survey-table__th--description">Description</th>
          <th scope="col" class="survey-table__th survey-table__th--actions">Actions</th>
        </tr>
      </thead>
      <tbody>
        @for (surveyType of surveyTypes; track surveyType.id) {
          <tr class="survey-table__row">
            <td class="survey-table__cell survey-table__cell--name" data-header="Name">
              <span>{{ surveyType.name }}</span>
            </td>
            <td class="survey-table__cell survey-table__cell--description" data-header="Description">
              <span class="survey-types-list__description">{{ surveyType.description || '—' }}</span>
            </td>
            <td class="survey-table__cell survey-table__cell--actions" data-header="Actions">
              <a class="survey-table__link" [routerLink]="['/admin/survey-types', surveyType.id, 'edit']">
                Edit (default)
              </a>
              <button type="button" class="btn survey-types-list__view-versions-btn" (click)="openVersionsModal(surveyType.id)" aria-label="View versions for {{ surveyType.name }}">
                View versions
              </button>
              <button type="button" class="btn survey-types-list__clone-btn" (click)="openCloneModal(surveyType)" aria-label="Create new survey from {{ surveyType.name }}">
                Create from this
              </button>
            </td>
          </tr>
        }
      </tbody>
    </table>
  </section>

  <!-- Versions modal -->
  @if (versionsModalTypeId !== null) {
    <div class="survey-types-list__modal-overlay" role="dialog" aria-modal="true" aria-labelledby="versions-modal-title" (keydown.escape)="closeVersionsModal()" (click)="closeVersionsModal()">
      <div class="survey-types-list__modal" (click)="$event.stopPropagation()">
        <div class="survey-types-list__modal-header">
          <h2 id="versions-modal-title" class="survey-types-list__modal-title">Versions — {{ getVersionsModalTypeName() }}</h2>
          <button type="button" class="survey-types-list__modal-close" (click)="closeVersionsModal()" aria-label="Close">×</button>
        </div>
        <div class="survey-types-list__modal-body">
          @let versions = getVersions(versionsModalTypeId!);
          @if (versions.length === 0) {
            <p class="survey-form__hint">No versions yet.</p>
          } @else {
            <ul class="survey-types-list__version-list">
              @for (v of versions; track v.id) {
                <li class="survey-types-list__version-item">
                  <span class="survey-types-list__version-label">v{{ v.versionNumber }} — {{ v.surveyStateCode }}</span>
                  <span class="survey-types-list__version-actions">
                    <a class="survey-table__link" [routerLink]="['/admin/survey-types', versionsModalTypeId, 'edit']" [queryParams]="{ versionId: v.id }" (click)="closeVersionsModal()">Edit</a>
                    @if (v.surveyStateCode === 'Published') {
                      <button type="button" class="btn survey-types-list__copy-btn" (click)="copyVersionUrlInModal(v.id)" aria-label="Copy survey URL for version {{ v.versionNumber }}">Copy link</button>
                    }
                  </span>
                </li>
              }
            </ul>
            @if (modalCopyFeedback) {
              <p class="survey-form__hint" role="status" aria-live="polite">{{ modalCopyFeedback }}</p>
            }
          }
        </div>
      </div>
    </div>
  }

  @if (errorMessage) {
    <p class="survey-form__error" role="alert">{{ errorMessage }}</p>
  }
  @if (isEmpty) {
    <p class="survey-types-list__empty" role="status" aria-live="polite">
      No survey types yet. Create one to get started.
    </p>
  }
  @if (loading) {
    <p role="status" aria-live="polite">Loading...</p>
  }

  <!-- Create new survey from existing (clone) modal -->
  @if (cloneModalSource !== null) {
    <div class="survey-types-list__modal-overlay" role="dialog" aria-modal="true" aria-labelledby="clone-modal-title" (keydown.escape)="closeCloneModal()" (click)="closeCloneModal()">
      <div class="survey-types-list__modal survey-types-list__modal--clone" (click)="$event.stopPropagation()">
        <div class="survey-types-list__modal-header">
          <h2 id="clone-modal-title" class="survey-types-list__modal-title">Create new survey from &quot;{{ getCloneModalSourceName() }}&quot;</h2>
          <button type="button" class="survey-types-list__modal-close" (click)="closeCloneModal()" aria-label="Close">×</button>
        </div>
        <div class="survey-types-list__modal-body">
          <p class="survey-form__hint">Enter a name and optional description for the new osaf_survey. Questions, answers, and prompts will be copied from the existing osaf_survey.</p>
          <form class="survey-form" (ngSubmit)="submitClone()" id="clone-survey-form">
            <div class="survey-form__group">
              <label class="survey-form__label" for="clone-new-name">New survey name <span class="survey-form__required" aria-hidden="true">*</span></label>
              <input
                class="survey-form__input"
                type="text"
                id="clone-new-name"
                required
                maxlength="200"
                placeholder="e.g. Outsource Finance Scorecard (Copy)"
                [(ngModel)]="cloneNewName"
                name="cloneNewName"
              />
            </div>
            <div class="survey-form__group">
              <label class="survey-form__label" for="clone-new-description">Description (optional)</label>
              <textarea
                class="survey-form__textarea survey-form__textarea--sm"
                id="clone-new-description"
                rows="2"
                placeholder="Optional description for the new survey"
                [(ngModel)]="cloneNewDescription"
                name="cloneNewDescription"
              ></textarea>
            </div>
            @if (cloneError) {
              <p class="survey-form__error" role="alert">{{ cloneError }}</p>
            }
            <div class="survey-form__actions">
              <button type="submit" class="btn btn-primary" [disabled]="cloneSubmitting">
                {{ cloneSubmitting ? 'Creating...' : 'Create survey' }}
              </button>
              <button type="button" class="btn survey-form__cancel" (click)="closeCloneModal()">
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  }
</main>
